import type { FilingHealthSummary, StateTaxObligationSummary } from '../types';
import { monthsAgo } from './dateMath';
import { formatWholeDay, wholeDayParts } from './complianceFormatters';

/**
 * Helpers for the obligation + clients-list domain. Pulled out of per-page
 * inline copies (`expectedPeriodMonths` / `expectedPeriodMonthsLocal`,
 * `isTestClient` / `isTestClientLocal`) so behavior matches across the
 * clients-list freshness pill, the dashboard at-risk panel, and the
 * client-detail follow-up banner.
 */

export type Freshness = 'recent' | 'stale' | 'overdue' | 'unknown';

/**
 * How many months between filings is "expected" for this frequency. Monthly
 * defaulted (most common cadence); unknown values fall back to monthly too.
 */
export function expectedPeriodMonths(frequency: string | null | undefined): number {
  switch ((frequency ?? '').toLowerCase()) {
    case 'monthly': return 1;
    case 'quarterly': return 3;
    case 'semi-annually':
    case 'semi annually':
    case 'semiannually': return 6;
    case 'annually':
    case 'annual': return 12;
    default: return 1;
  }
}

/**
 * Freshness verdict scaled to the client's filing cadence. Stale at 2×; overdue
 * at 3×+. A Monthly client is stale at 2 months, Quarterly at 4, Annual at 13 —
 * proportional to how often they actually file.
 */
export function freshnessFor(
  monthsSinceLastFiled: number,
  frequency: string | null | undefined,
): Freshness {
  if (!Number.isFinite(monthsSinceLastFiled)) return 'overdue';
  const period = expectedPeriodMonths(frequency);
  if (monthsSinceLastFiled <= period) return 'recent';
  if (monthsSinceLastFiled <= period * 2) return 'stale';
  return 'overdue';
}

/**
 * Sort key for "last filed" — older / never-filed clients sort to the top
 * when descending (so triage view surfaces at-risk clients first).
 */
export function freshnessSortKey(h: FilingHealthSummary | undefined): number {
  if (!h?.lastFiledMonth) return Number.MAX_SAFE_INTEGER;
  return monthsAgo(h.lastFiledMonth);
}

/**
 * Earliest non-terminal due date across this client's tasks. Returned as a
 * sortable number (ms since epoch); clients with no upcoming filings sort to
 * the end. Powers the "Next due" column display + sort comparator.
 */
export function nextDueSortKey(nextDueMs: number | undefined): number {
  return nextDueMs ?? Number.MAX_SAFE_INTEGER;
}

/**
 * Test/placeholder data we don't want polluting DASHBOARD aggregates (tiles,
 * at-risk triage, billing standing). Currently the only obvious one in the
 * tenant is "Test Company"; the match is intentionally conservative so we don't
 * accidentally exclude real clients with "test" in their real names.
 *
 * Scope (Christian, 2026-10-20): test clients are hidden from EVERY staff list — dashboard,
 * tasks list, clients list — and the nightly generator skips them (server-side TestClients.cs).
 * The task-sync pilot still runs on Test Company; whoever runs it opens a list with
 * `?showTestClients=1` (see includeTestClients), which no staff member is shown or needs.
 */
/**
 * The pilot's deliberate opt-in: a list shows test clients only when its URL carries
 * `showTestClients=1`. Read from the URL rather than a toggle so it never appears in a staff
 * member's UI, and never sticks in a browser.
 */
export function includeTestClients(search: string = typeof window === 'undefined' ? '' : window.location.search): boolean {
  return new URLSearchParams(search).get('showTestClients') === '1';
}

export function isTestClient(name: string | null | undefined): boolean {
  if (!name) return false;
  const n = name.trim().toLowerCase();
  return n === 'test company' || n.startsWith('test company ') || n.startsWith('zz test');
}

/**
 * Reporting-since label for the obligations table — falls back to dash when
 * neither effectiveStartDate nor reportingSinceDate is set.
 */
export function formatReportingSince(o: StateTaxObligationSummary, dash: string): string {
  const dateStr = o.effectiveStartDate ?? o.reportingSinceDate;
  if (!dateStr) return dash;
  // A whole day (BL-142): "2026-08-01T00:00:00Z" is August, not July in Eastern time.
  if (!wholeDayParts(dateStr)) return dash;
  return formatWholeDay(dateStr, { month: 'short', year: 'numeric' });
}

/**
 * The form named in "drafts can't be calculated automatically for {{form}}". A non-Florida filing carries its state
 * ("Sales Tax (GA)"), because the draft engine is Florida's (BL-119): without it the message reads as if the tax type
 * itself could never be calculated. Blank state is Florida, as on the server (`FormProfiles.IsFlorida`).
 */
export function unsupportedFormLabel(f: { obligationDisplayName?: string | null; obligationType?: string | null; state?: string | null }): string {
  const name = f.obligationDisplayName || f.obligationType || '';
  const state = (f.state ?? '').trim();
  const florida = state === '' || /^(fl|florida)$/i.test(state);
  return florida ? name : `${name} (${state.toUpperCase()})`;
}
